import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import IconAddLink from '../../../components/icons/IconAddLink';
import IconCastEducation from '../../../components/icons/IconCastEducation';
import LessonDetailModal from '../LessonDetailModal';
import { getTopicLessons, createLessonTopic, deleteLessonTopic } from './lessonTopicsApi';
import language from '../../../variables/language';
import '../../../css/lesson-topic-link.css';

function formatDate(lesson) {
   return lesson.date ? DateTime.fromISO(lesson.date).toFormat('dd.MM.yyyy') : '';
}
function formatTime(lesson) {
   return lesson.start ? lesson.start + (lesson.end ? '–' + lesson.end : '') : '';
}
function isPast(lesson) {
   if (!lesson.date) return false;
   const end = DateTime.fromFormat(lesson.date + ' ' + (lesson.end || lesson.start || '23:59'), 'yyyy-MM-dd HH:mm');
   return end.isValid && end < DateTime.now();
}
function isoWeekday(lesson) {
   if (!lesson.date) return null;
   const dt = DateTime.fromISO(lesson.date);
   return dt.isValid ? dt.weekday : null;
}
// Same weekday color rule as LessonDetailModal.jsx's own weekday pill -
// reused via the same global CSS classes (student-card.css) rather than
// re-defining the colors here.
const weekdayColorClass = (weekday) => {
   if (weekday === 6) return 'lesson-detail-weekday-saturday';
   if (weekday === 7) return 'lesson-detail-weekday-sunday';
   return 'lesson-detail-weekday-workday';
};

// Mirror of LessonTopicFlyout.jsx for the opposite direction: link THIS
// topic to one or more of the student's own scheduled Lessons. Rendered
// once per topic in WorkspaceTopicSection.jsx's header, in-flow right
// after the description (same spot courses/preview/TopicLessonBadges.jsx
// uses on the admin course-preview side, reusing its exact
// .course-preview-lesson-badge* classes so a linked lesson looks identical
// in both places).
//
// Already-linked lessons render as ALWAYS-VISIBLE badges (no click needed
// to discover them - a teacher scanning the workspace should see "covered
// in lesson X" at a glance) with their own remove (x); the icon trigger +
// flyout only ever handles ADDING a new one, same split as
// LessonTopicFlyout.jsx's header pills vs. its own add-only flyout.
//
// LessonTopic itself carries no student field (a Lesson can have several
// participants via Event), so both fetches below run unconditionally (not
// lazily on open) and every badge is cross-checked against THIS student's
// own lessons before rendering - a link created on a different
// participant's workspace for a group lesson must never appear (or be
// addable) here, only links that are genuinely this student's own lesson.
export default function TopicLessonFlyout({ topicId, studentId }) {
   const [open, setOpen] = useState(false);
   const [studentLessons, setStudentLessons] = useState(null);
   const [links, setLinks] = useState([]);
   const [query, setQuery] = useState('');
   const [openLessonId, setOpenLessonId] = useState(null);
   const wrapRef = useRef(null);
   const lang = language[language.lang];

   const loadLinks = () => {
      getTopicLessons(topicId)
         .then(response => setLinks(Array.isArray(response.data) ? response.data : []))
         .catch(() => setLinks([]));
   };
   const loadStudentLessons = () => {
      axios.get('/api/events?student=' + studentId, { withCredentials: true })
         .then(response => {
            const events = Array.isArray(response.data) ? response.data : [];
            const byId = new Map();
            events.forEach(ev => { if (ev.lesson) byId.set(ev.lesson.id, ev.lesson); });
            setStudentLessons(Array.from(byId.values()));
         })
         .catch(() => setStudentLessons([]));
   };

   useEffect(() => {
      loadLinks();
      loadStudentLessons();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [topicId, studentId]);

   useEffect(() => {
      const handleOutside = (e) => {
         if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
   }, []);

   const studentLessonIds = new Set((studentLessons || []).map(l => l.id));
   const visibleLinks = links.filter(lt => studentLessonIds.has(lt.lesson.id));
   const linkedLessonIds = new Set(links.map(lt => lt.lesson.id));
   const available = (studentLessons || []).filter(l => !linkedLessonIds.has(l.id));
   const filteredAvailable = available.filter(l => {
      if (!query.trim()) return true;
      return (formatDate(l) + ' ' + formatTime(l)).toLowerCase().includes(query.trim().toLowerCase());
   });

   const handlePick = (lesson) => {
      createLessonTopic(lesson.id, topicId).then(() => { loadLinks(); setQuery(''); }).catch(() => {});
   };
   const handleRemove = (e, linkId) => {
      e.stopPropagation();
      deleteLessonTopic(linkId).then(loadLinks).catch(() => {});
   };

   return (
      <div className='course-preview-lesson-badges'>
         {visibleLinks.map(lt => {
            const wd = isoWeekday(lt.lesson);
            return (
            <div className={'course-preview-lesson-badge' + (isPast(lt.lesson) ? ' past' : '')} key={lt.id} onClick={() => setOpenLessonId(lt.lesson.id)}>
               <span className='course-preview-lesson-badge-icon'><IconCastEducation style={{ fill: 'var(--secondarytext-color)', width: 40, height: 40 }} /></span>
               <div style={{display:'flex',flexDirection:'column',gap:2}}>
               {wd ? <span className={'course-preview-lesson-badge-weekday ' + weekdayColorClass(wd)}>{lang.weekDaysFull[wd - 1]}</span> : null}
               <span className='course-preview-lesson-badge-date'>{formatDate(lt.lesson)}</span>
               <span className='course-preview-lesson-badge-time'>{formatTime(lt.lesson)}</span>
               </div>

               <span className='lesson-topic-chip-remove' onClick={(e) => handleRemove(e, lt.id)}>×</span>
            </div>
            );
         })}
         <span className='lesson-topic-trigger-wrap' ref={wrapRef}>
            <span className={'lesson-topic-trigger-btn' + (open ? ' active' : '')} onClick={() => { setOpen(v => !v); setQuery(''); }} title={lang.workspaceTopicLessons}>
               <IconAddLink style={{ fill: open ? 'var(--lighttext-color)' : 'var(--secondarytext-color)', width: 15, height: 15 }} />
            </span>
            {open ? (
               <div className='lesson-topic-flyout open-left' onMouseDown={e => e.stopPropagation()}>
                  <p className='lesson-topic-flyout-title'>{lang.workspaceTopicLessons}</p>
                  {studentLessons === null ? (
                     <p className='lesson-topic-flyout-empty'>{lang['Loading ...']}</p>
                  ) : studentLessons.length === 0 ? (
                     <p className='lesson-topic-flyout-empty'>{lang.workspaceTopicLessonsNone}</p>
                  ) : (
                     <>
                        <input className='lesson-topic-search-input' type='text' placeholder={lang.workspaceTopicLessonsAddPlaceholder}
                           value={query} onChange={e => setQuery(e.target.value)} />
                        <div className='lesson-topic-option-list'>
                           {filteredAvailable.length === 0 ? (
                              <p className='lesson-topic-flyout-empty'>{lang['Empty list']}</p>
                           ) : filteredAvailable.map(l => (
                              <div key={l.id} className='lesson-topic-option' onClick={() => handlePick(l)}>
                                 {formatDate(l)} {formatTime(l)}
                              </div>
                           ))}
                        </div>
                     </>
                  )}
               </div>
            ) : ''}
         </span>
         <LessonDetailModal isOpen={!!openLessonId} lessonId={openLessonId}
            onClose={() => setOpenLessonId(null)} onSaved={() => {}} />
      </div>
   );
}
